import React, { useState, useEffect, useMemo, useRef, memo } from 'react';
import { Box, useTheme, keyframes } from '@mui/material';
import { posterCollage } from '../../tokens/designTokens';

interface ImageShimmerProps {
  src?: string | null;
  alt?: string;
  width?: string | number;
  height?: string | number;
  aspectRatio?: string;
  borderRadius?: number | string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  sx?: any;
  onLoad?: () => void;
  onError?: () => void;
  priority?: boolean; // For images that should load immediately
  className?: string;
  style?: React.CSSProperties;
}

// Shimmer animation keyframes
const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

// Global image cache to prevent redundant loads
const imageCache = new Map<string, 'loading' | 'loaded' | 'error'>();

const ImageShimmer: React.FC<ImageShimmerProps> = memo(({
  src,
  alt = '',
  width = '100%',
  height = '100%',
  aspectRatio,
  borderRadius = 0,
  objectFit = 'cover',
  sx = {},
  onLoad,
  onError,
  priority = false,
  className,
  style,
}) => {
  const theme = useTheme();

  // Clean src - handle null, undefined, and 'N/A' values
  const cleanSrc = useMemo(() => {
    if (!src || src === 'N/A' || src.trim() === '') {
      return null;
    }
    return src.trim();
  }, [src]);

  // Simple state management
  const [imageLoaded, setImageLoaded] = useState(() => {
    return cleanSrc ? imageCache.get(cleanSrc) === 'loaded' : false;
  });

  const [imageError, setImageError] = useState(() => {
    return cleanSrc ? imageCache.get(cleanSrc) === 'error' : false;
  });

  const [isLoading, setIsLoading] = useState(() => {
    if (!cleanSrc) return false;
    const cached = imageCache.get(cleanSrc);
    return !cached || cached === 'loading';
  });

  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track if the iOS WebP fallback retry has been attempted for the current src
  const hasTriedFallbackRef = useRef(false);
  // Current src URL the rendered <img> should resolve, so iOS retry can swap to a cache-bust URL
  const [renderSrc, setRenderSrc] = useState<string | null>(cleanSrc);

  // Sync internal state to the incoming src. The actual network fetch is performed by the
  // rendered <img> element (which honors loading="lazy" so off-screen cards don't request
  // their poster until they intersect the viewport). We only mirror cache state here.
  useEffect(() => {
    hasTriedFallbackRef.current = false;
    setRenderSrc(cleanSrc);

    if (!cleanSrc) {
      setImageLoaded(false);
      setImageError(false);
      setIsLoading(false);
      return;
    }

    const cached = imageCache.get(cleanSrc);
    if (cached === 'loaded') {
      setImageLoaded(true);
      setImageError(false);
      setIsLoading(false);
      return;
    }

    if (cached === 'error') {
      setImageLoaded(false);
      setImageError(true);
      setIsLoading(false);
      return;
    }

    setImageLoaded(false);
    setImageError(false);
    setIsLoading(true);
    imageCache.set(cleanSrc, 'loading');

    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }

    // Timeout watchdog in case the <img> never fires onload/onerror (rare; mostly iOS).
    const isIOSCapacitor = typeof window !== 'undefined' &&
      (window as any).Capacitor?.getPlatform?.() === 'ios';
    const timeoutMs = isIOSCapacitor ? 15000 : 10000;
    loadingTimeoutRef.current = setTimeout(() => {
      if (imageCache.get(cleanSrc) === 'loading') {
        imageCache.set(cleanSrc, 'error');
        setImageError(true);
        setIsLoading(false);
        onError?.();
      }
    }, timeoutMs);

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, [cleanSrc, onError]);

  // Handlers wired to the <img> element directly, so the browser does only one fetch and
  // lazy-loading is respected.
  const handleImgLoad = () => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    if (cleanSrc) imageCache.set(cleanSrc, 'loaded');
    setImageLoaded(true);
    setIsLoading(false);
    setImageError(false);
    onLoad?.();
  };

  const handleImgError = () => {
    if (!cleanSrc) return;

    const isIOSCapacitor = typeof window !== 'undefined' &&
      (window as any).Capacitor?.getPlatform?.() === 'ios';

    // iOS WebP decode retry: cache-bust once, then give up.
    if (isIOSCapacitor && !hasTriedFallbackRef.current && cleanSrc.includes('tmdb.org')) {
      hasTriedFallbackRef.current = true;
      console.log('🔄 ImageShimmer: WebP decode may have failed on iOS, retrying with cache bust');
      const separator = cleanSrc.includes('?') ? '&' : '?';
      setRenderSrc(`${cleanSrc}${separator}_t=${Date.now()}`);
      return;
    }

    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    imageCache.set(cleanSrc, 'error');
    setImageError(true);
    setIsLoading(false);
    onError?.();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  // Shimmer animation
  const shimmerAnimation = useMemo(() => ({
    background: theme.palette.mode === 'dark'
      ? posterCollage.shimmer.dark.overlay
      : posterCollage.shimmer.light.overlay,
    backgroundSize: '200px 100%',
    animation: `${shimmer} 1.5s infinite linear`,
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    opacity: isLoading ? 1 : 0,
    transition: 'opacity 0.3s ease',
    borderRadius: 'inherit',
  }), [isLoading, theme.palette.mode]);

  // Container styles
  const containerStyles = useMemo(() => ({
    position: 'relative',
    width,
    height,
    aspectRatio,
    borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.mode === 'dark'
      ? posterCollage.placeholder.dark
      : posterCollage.placeholder.light,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...sx,
  }), [width, height, aspectRatio, borderRadius, sx, theme.palette.mode]);

  return (
    <Box
      className={className}
      style={style}
      sx={containerStyles}
    >
      {/* Actual image — single network fetch performed by the browser, respecting loading="lazy" */}
      {renderSrc && (
        <Box
          component="img"
          src={renderSrc}
          alt={alt}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          onLoad={handleImgLoad}
          onError={handleImgError}
          sx={{
            width: '100%',
            height: '100%',
            objectFit,
            opacity: imageLoaded ? 1 : 0,
            transition: imageLoaded ? 'opacity 0.3s ease-in-out' : 'none',
            zIndex: 3,
            position: 'relative',
            userSelect: 'none',
            pointerEvents: 'none',
            // Safari compatibility
            WebkitBackfaceVisibility: 'hidden',
            WebkitTransform: 'translateZ(0)',
          }}
        />
      )}

      {/* Shimmer overlay */}
      <Box sx={shimmerAnimation} />

      {/* Silent placeholder for errors - NO error text */}
      {(!cleanSrc || imageError) && !isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: theme.palette.mode === 'dark'
              ? posterCollage.placeholder.dark
              : posterCollage.placeholder.light,
            zIndex: 2,
          }}
        />
      )}
    </Box>
  );
});

ImageShimmer.displayName = 'ImageShimmer';

export default ImageShimmer; 